import { gvector } from '../../js/open-source/gvector.js';
import { NJUNS_STEPS_FIELD } from '../../modules/NjunsApiLayerTranslation.js';

/**
 * A gvector layer that draws NJUNS tickets from `options.fetchFeatures` and stops fetching once that resolves to null.
 */
export const NjunsTicketLayer = gvector.GeoJSONLayer.extend({
  initialize: function (options) {
    if (typeof options.fetchFeatures !== 'function') throw new Error('No "fetchFeatures" function found.');

    gvector.Layer.prototype.initialize.call(this, options);

    // Create an array to hold the features
    this._vectors = [];
    this._vectorListeners = [];

    if (this.options.map) {
      if (this.options.scaleRange && this.options.scaleRange instanceof Array && this.options.scaleRange.length === 2) {
        const zoom = this.options.map.getZoom();
        const [minZoom, maxZoom] = this.options.scaleRange;
        this.options.visibleAtScale = zoom >= minZoom && zoom <= maxZoom;
      }
      this._show();
    }
  },

  options: {
    fetchFeatures: null,
    errorCallback: null
  },

  _getFeatures: function () {
    if (this._stoppedFetching) return;
    const bounds = this.options.map?.getBounds();
    if (!bounds) return;
    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();

    this.options.loadingCallback?.(true, this);
    this.options
      .fetchFeatures({ north: northEast.lat(), east: northEast.lng(), south: southWest.lat(), west: southWest.lng() })
      .then((featureCollection) => {
        if (featureCollection == null) {
          this._stoppedFetching = true;
          return;
        }
        // A response for bounds the map has left would mark the new bounds as queried, so their own response gets dropped
        const currentBounds = this.options.map?.getBounds();
        if (currentBounds?.equals(bounds)) this._processFeatures(featureCollection);
      })
      .catch((error) => this.options.errorCallback?.(error, this))
      .finally(() => this.options.loadingCallback?.(false, this));
  }
});

/**
 * Build an info window template that shows the display fields, then the steps table used by nearby-ticket markers.
 * @param {string[]} displayFields The fields to show, in order.
 * @returns {(properties: object) => string} The template that turns feature properties into info window HTML.
 */
export function buildNjunsTicketInfoWindowTemplate(displayFields) {
  return (properties) => {
    const rows = displayFields
      .filter((field) => field !== NJUNS_STEPS_FIELD)
      .map((field) => `<tr><td><b>${escapeHTML(field)}:</b></td><td>${escapeHTML(formatValue(properties?.[field]))}</td></tr>`)
      .join('');
    let output = `<h3>NJUNS Ticket</h3><table>${rows}</table>`;

    const steps = properties?.[NJUNS_STEPS_FIELD];
    if (displayFields.includes(NJUNS_STEPS_FIELD) && Array.isArray(steps) && steps.length) {
      const stepRows = steps
        .map(
          (step) =>
            `<tr><td>${escapeHTML(step.member)}</td><td>${escapeHTML(step.sequence)}</td><td>${escapeHTML(step.status)}</td><td>${escapeHTML(step.type)}</td></tr>`
        )
        .join('');
      output += `<table><tr><td><b>Steps:</b></td></tr></table><table>${stepRows}</table>`;
    }
    return output;
  };
}

function formatValue(value) {
  if (value != null && typeof value === 'object') return JSON.stringify(value);
  return value;
}

function escapeHTML(value) {
  if (value == null) return '';
  let escapedValue = String(value);
  escapedValue = escapedValue.replace(/&/g, '&amp;');
  escapedValue = escapedValue.replace(/</g, '&lt;');
  escapedValue = escapedValue.replace(/>/g, '&gt;');
  return escapedValue.replace(/"/g, '&quot;');
}
